import { useEffect, useRef } from 'react';
import { SidebarMenuItem } from './SidebarMenuItem';
import './SidebarMenu.css';

export const SidebarMenu = ({ 
  items, 
  bottomContent,
  isSticky = false,
  className = '' 
}) => {
  const sidebarRef = useRef(null);
  const hasWarnedRef = useRef(false);
  const resizeSensorRef = useRef(null);
  const initDelayTimeoutRef = useRef(null);
  const pluginReadyIntervalRef = useRef(null);
  const pluginReadyTimeoutRef = useRef(null);

  const destroyResizeSensor = () => {
    if (resizeSensorRef.current) {
      resizeSensorRef.current.detach();
      resizeSensorRef.current = null;
    }
  };

  const resetSidebarStyles = () => {
    const sidebarEl = sidebarRef.current;
    if (!sidebarEl) {
      return;
    }

    sidebarEl.style.minHeight = '';

    const stickyInner = sidebarEl.querySelector('.theiaStickySidebar');
    if (stickyInner) {
      stickyInner.style.position = '';
      stickyInner.style.width = '';
      stickyInner.style.transform = '';
      stickyInner.style.left = '';
      stickyInner.style.top = '';
    }
  };

  const initializeStickySidebar = ($sidebar) => {
    resetSidebarStyles();
    $sidebar.theiaStickySidebar({
      containerSelector: '.two-column-layout',
      additionalMarginTop: 120
    });
  };

  const clearInitDelayTimeout = () => {
    if (initDelayTimeoutRef.current) {
      clearTimeout(initDelayTimeoutRef.current);
      initDelayTimeoutRef.current = null;
    }
  };

  const clearPluginReadyPolling = () => {
    if (pluginReadyIntervalRef.current) {
      clearInterval(pluginReadyIntervalRef.current);
      pluginReadyIntervalRef.current = null;
    }
    if (pluginReadyTimeoutRef.current) {
      clearTimeout(pluginReadyTimeoutRef.current);
      pluginReadyTimeoutRef.current = null;
    }
  };

  const scheduleStickyInitialization = () => {
    if (!sidebarRef.current) {
      return false;
    }

    const $ = window?.jQuery;
    if (!$ || !$.fn?.theiaStickySidebar) {
      return false;
    }

    const $sidebar = $(sidebarRef.current);

    const setupStickySidebar = () => {
      destroyResizeSensor();
      initializeStickySidebar($sidebar);

      const ResizeSensor = window?.ResizeSensor;
      if (ResizeSensor) {
        resizeSensorRef.current = new ResizeSensor(sidebarRef.current, () => {
          initializeStickySidebar($sidebar);
        });
      }
    };

    clearInitDelayTimeout();
    initDelayTimeoutRef.current = setTimeout(setupStickySidebar, 200);

    return true;
  };

  useEffect(() => {
    clearInitDelayTimeout();
    clearPluginReadyPolling();
    destroyResizeSensor();

    if (!isSticky) {
      resetSidebarStyles();
      return;
    }

    const initialized = scheduleStickyInitialization();

    if (!initialized) {
      if (!hasWarnedRef.current) {
        console.warn(
          'SidebarMenu: Theia Sticky Sidebar plugin is not available. See https://github.com/WeCodePixels/theia-sticky-sidebar'
        );
        hasWarnedRef.current = true;
      }

      const pollForPlugin = () => {
        if (scheduleStickyInitialization()) {
          clearPluginReadyPolling();
        }
      };

      pluginReadyIntervalRef.current = setInterval(pollForPlugin, 150);
      pluginReadyTimeoutRef.current = setTimeout(() => {
        clearPluginReadyPolling();
      }, 5000);
    }

    return () => {
      clearInitDelayTimeout();
      clearPluginReadyPolling();
      destroyResizeSensor();
      resetSidebarStyles();
    };
  }, [isSticky]);

  const sidebarClassName = ['sidebar-menu', className].filter(Boolean).join(' ').trim();
  const menuItems = Array.isArray(items) ? items : [];

  return (
    <div className={sidebarClassName} ref={sidebarRef}>
      <div className="sidebar-menu__inner theiaStickySidebar">
        <nav className="sidebar-menu__list">
          {menuItems.map((item) => (
            <SidebarMenuItem
              key={item.id}
              label={item.label}
              isActive={item.isActive}
              isLoading={item.isLoading}
              onClick={item.onClick}
            />
          ))}
        </nav>
        {bottomContent && (
          <div className="sidebar-menu__bottom-content">
            {bottomContent}
          </div>
        )}
      </div>
    </div>
  );
};

